import { pageEntityByIdSelector } from '@mm/state/selectors/pageSelectors';
import { activeChatbotSelector } from '@mm/state/selectors/viewSelectors';
import { widgetsByDialogueSelector } from '@mm/state/selectors/widgetSelectors';
import { selectAudienceByIdOrIds } from '@mm/state/selectors/messengerAdSelectors';
import { useCampaignCRUDActions } from '@mm/state/actions/CampaignsCRUDActions';
import { CloseIcon, TrashIcon } from '@mm/icons';
import { Heading } from '@mm/components';
import { useViewActions } from '@mm/state/actions/viewActions';
import { UICampaignItem } from '@mm/state/reducers/campaignsOverview';
import { StoreState } from '@mm/state/store-interfaces';
import {
  CampaignSchedulerAttribute,
  CampaignScheduleType,
  PlatformType,
} from '@mm/state/model/CampaignsModel';
import {
  CampaignTypeWithMappedPurposeAndDisplayStatus,
  selectCampaignById,
} from '@mm/state/selectors/campaignSelectors';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { uniqueId } from 'lodash/fp';
import { DragDropContext } from 'react-beautiful-dnd';
import moment from 'moment';
import CollapsibleContentContainer from '../../../../../common/components/CollapsibleContentContainer/CollapsibleContentContainer';
import { SlideoutMenu } from '../../components/SlideoutMenu';
import styles from './ChatBlasterSlideoutEditor.module.css';

import { ChatBlasterSteps } from '../../../../features/campaigns/panels/ChatBlasterDetailsPanel/ChatBlasterSteps/ChatBlasterSteps';
import {
  getContactsCount,
  mapStateCampaignForUpdate,
  validateCampaign,
} from '../../../../features/campaigns/panels/ChatBlasterDetailsPanel/ChatBlasterDetailsPanel';
import { findPurposeId } from '../../../../features/campaigns/components/BlastTypeSelector/BlastTypeSelector';

import { ChatBlasterDialogueEditor } from '../../../../features/campaigns/panels/ChatBlasterDetailsPanel/ChatBlasterDialogueEditor/ChatBlasterDialogueEditor';
import { SmsIphonePreview } from '../../../../features/campaigns/components/SmsIphonePreview/SmsIphonePreview';
import {
  getMMdndEvents,
  MMdndContext,
} from '../../../../../common/helpers/MMdndContext';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { useDeleteCampaign } from '../../../../features/beta/modules/Campaigns/use-delete-campaign';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';

export type ChatBlasterSlideoutEditorProps = {
  campaignId: number;
  pageId: number;
};

export const ChatBlasterSlideoutEditor = ({
  campaignId,
  pageId,
}: ChatBlasterSlideoutEditorProps) => {
  const chatbot = useSelector(activeChatbotSelector);
  const campaign = useSelector((state: StoreState) =>
    selectCampaignById(state, Number(campaignId)),
  )!;
  const dialogue = useSelector((state: StoreState) =>
    pageEntityByIdSelector(state, String(pageId)),
  );
  const viewActions = useViewActions();
  const dndEvents = getMMdndEvents();
  const { deleteCampaign } = useDeleteCampaign();
  const [activePreview, setActivePreview] = useState('sms');
  const [stateCampaign, setStateCampaign] = useState(campaign);
  const [diffKey, setDiffKey] = useState(uniqueId('state-campaign-'));
  const { setRef } = useSlideoutOnClose();

  const setCampaign = (
    update: CampaignTypeWithMappedPurposeAndDisplayStatus,
  ) => {
    const id = uniqueId('state-campaign-');
    setStateCampaign(update);
    setDiffKey(id);
  };

  const widgets = useSelector((state: StoreState) =>
    widgetsByDialogueSelector(dialogue.id, state.widget.entities),
  );
  const campaignCRUDActions = useCampaignCRUDActions();

  const validationErrors = validateCampaign(stateCampaign);
  if (!widgets.length) {
    validationErrors.dialogue = 'Dialogue Empty';
  }

  const purposes = useSelector(
    (state: StoreState) => state.campaignPurposes.purposes,
  );

  const purposeId = findPurposeId(
    purposes,
    stateCampaign.purpose,
    stateCampaign.purposeName,
  );

  const platforms = {
    facebook: false,
    sms: true,
  };

  const [audience] = useSelector((state: StoreState) =>
    selectAudienceByIdOrIds(state, stateCampaign.audienceId || null),
  );

  const targetAudienceSize = getContactsCount(
    purposeId,
    audience,
    platforms,
    stateCampaign.tags?.[0]?.name,
  );

  const isSaveDisabled = !stateCampaign.name || !stateCampaign.audienceId;
  const isCampaignValid = !Object.values(validationErrors).length;
  const onSaveCampaign = () => {
    if (chatbot.id) {
      return campaignCRUDActions.updateCampaign(
        mapStateCampaignForUpdate(stateCampaign),
        chatbot.id,
      );
    }
  };

  const onPurposeChange = (purpose: number, tagName?: string) => {
    setCampaign({
      ...stateCampaign,
      purpose,
      tags: tagName ? [{ name: tagName }] : [],
    });
  };
  const onTagNameChange = useCallback(
    (name?: string) => {
      setCampaign({ ...stateCampaign, tags: name ? [{ name }] : [] });
    },
    [stateCampaign.purpose, diffKey, stateCampaign],
  );

  const onAudienceSelect = (audienceId: number) => {
    setCampaign({
      ...stateCampaign,
      audienceId,
      campaignAudiencesAttributes: [{ audienceId, kind: 'included' }], // api service reacts to audience update only to this field
    });
  };

  const onRecipientTimeZoneChange = (shouldUse: boolean) => {
    setCampaign({
      ...stateCampaign,
      scheduleType: shouldUse ? 'timezone' : 'global',
    });
  };

  const onSelectedDateChange = useCallback(
    (scheduledAt: moment.Moment) => {
      setCampaign({
        ...stateCampaign,
        schedulersAttributes: [
          {
            ...stateCampaign.schedulersAttributes?.[0],
            scheduledAt: scheduledAt.toString(),
          } as CampaignSchedulerAttribute,
        ],
      });
    },
    [diffKey, stateCampaign],
  );

  const onPlatformsChange = (platforms: PlatformType[]) => {
    setCampaign({ ...stateCampaign, platforms });
  };

  const onSendImmediatelyChange = (shouldSend: boolean) => {
    const newCampaign = {
      ...stateCampaign,
      scheduleType: shouldSend ? 'none' : ('timezone' as CampaignScheduleType),
    };

    const scheduledAt = !shouldSend
      ? String(moment().add(1, 'day').hours(9).startOf('hour'))
      : null;

    newCampaign.schedulersAttributes = [
      {
        ...stateCampaign.schedulersAttributes?.[0],
        scheduledAt,
      } as CampaignSchedulerAttribute,
    ];

    setCampaign(newCampaign);
  };

  return (
    <SlideoutMenu className={styles.menu} setRef={setRef}>
      <div className={styles.headerContent}>
        <Heading className={styles.headerTitle} size={3}>
          SMS Blaster
        </Heading>
        <div className={styles.headerButtons}>
          <TrashIcon
            className={styles.headerDeleteButton}
            onClick={() =>
              deleteCampaign({
                id: campaign.id,
                kind: 'blaster',
                name: campaign.name,
              } as UICampaignItem)
            }
          />
          <PrimaryButton
            color={ButtonColor.transparent}
            onClick={onSaveCampaign}
            className={styles.saveBtn}
            disabled={isSaveDisabled}
          >
            Save
          </PrimaryButton>
          <div
            onClick={viewActions.hideSlideoutMenu}
            className={styles.closeIcon}
          >
            <CloseIcon color="#8c8c8c" />
          </div>
        </div>
      </div>
      <SlideoutMenu.Content className={styles.content}>
        <div className={styles.configArea}>
          <CollapsibleContentContainer
            orderNumber="1"
            containerName="SMS Blaster Settings"
            className={styles.collapsibleContainer}
            useCircleNumberStyle
          >
            <ChatBlasterSteps
              version={diffKey}
              errors={validationErrors}
              campaign={stateCampaign}
              purposeId={purposeId}
              dialogue={dialogue}
              targetAudienceSize={targetAudienceSize}
              onPlatformsChange={onPlatformsChange}
              onPurposeChange={onPurposeChange}
              onTagNameChange={onTagNameChange}
              onAudienceSelect={onAudienceSelect}
              onSelectedDateChange={onSelectedDateChange}
              onRecipientTimeZoneChange={onRecipientTimeZoneChange}
              onSendImmediatelyChange={onSendImmediatelyChange}
              setActivePreview={setActivePreview}
            />
          </CollapsibleContentContainer>

          <CollapsibleContentContainer
            orderNumber="2"
            containerName="Create a Dialogue"
            className={styles.collapsibleContainer}
            useCircleNumberStyle
          >
            <div className={styles.dialoguePreviewContainer}>
              <div className={styles.dialogueContainer}>
                <MMdndContext.Provider value={dndEvents}>
                  <DragDropContext
                    onDragStart={dndEvents.onDragStart}
                    onDragUpdate={dndEvents.onDragUpdate}
                    onDragEnd={dndEvents.onDragEnd}
                  >
                    <ChatBlasterDialogueEditor
                      purposeId={purposeId}
                      campaign={stateCampaign}
                      version={diffKey}
                      dialogue={dialogue}
                      errors={validationErrors}
                      usedInSlideout
                      hideDialogueSelector
                    />
                  </DragDropContext>
                </MMdndContext.Provider>
              </div>
              <SmsIphonePreview
                className={styles.smsPreview}
                campaignId={campaignId}
                schedulerId={stateCampaign.schedulersAttributes?.[0].id}
                schedulerIndex={0}
                activeTestNumbers={
                  stateCampaign.schedulersAttributes?.[0].testNumbers
                }
                onSaveCampaign={onSaveCampaign}
                destinationPageId={dialogue.id}
              />
            </div>
          </CollapsibleContentContainer>
        </div>
      </SlideoutMenu.Content>
    </SlideoutMenu>
  );
};
